import { subscribeToThreads } from '@tiptap-pro/extension-comments'
import { type TCollabThread, TiptapCollabProvider } from '@tiptap-pro/provider'
import { compareDesc, isAfter } from 'date-fns'
import { differenceBy } from 'lodash'
import { useEffect, useRef, useState } from 'react'
import type { User } from '@opallabs/concepts'
import { DocumentResourceType } from '../types'

export type ThreadData = {
  /**
   * The ID of the resource that the thread is associated with.
   */
  resource_id: string
  /**
   * The type of the resource that the thread is associated with.
   */
  resource_type: DocumentResourceType
  /**
   * The ID of the author of the thread.
   */
  author_id: string
}

export type ThreadCommentData = {
  author_id: string
  userName: string
  userAvatar?: string
  pending: boolean
  isUserEdit: boolean
}

type UseThreadsParams = {
  onChangeThread: (threadId: string | null) => void
  provider: TiptapCollabProvider | undefined
  user?: User | null
}

export const useThreads = ({
  onChangeThread,
  provider,
  user,
}: UseThreadsParams) => {
  const mountTime = useRef(new Date())
  const [threads, setThreads] = useState<
    TCollabThread<ThreadData, ThreadCommentData>[]
  >([])

  useEffect(() => {
    if (!provider) {
      return
    }

    const unsubscribe = subscribeToThreads({
      provider,
      callback: (threads: TCollabThread<ThreadData, ThreadCommentData>[]) => {
        let activeThreadId: string | null = null
        setThreads((prevState) => {
          // Watch for threads created by the current user,
          // after the document has loaded
          if (user?.uuid != null) {
            activeThreadId =
              differenceBy(threads, prevState, 'id')
                .filter((thread) =>
                  isAfter(new Date(thread.createdAt), mountTime.current)
                )
                .sort((a, b) => compareDesc(a.createdAt, b.createdAt))
                .find((thread) => thread.data.author_id === user?.uuid)?.id ??
              null
          }

          return threads
        })

        // If a new thread was found, set it as active
        // Wait until next tick to let the threads state update first
        setTimeout(() => {
          if (activeThreadId != null) {
            onChangeThread(activeThreadId)
          }
        }, 50)
      },
      getThreadsOptions: {
        types: ['unarchived'],
      },
    })

    return () => {
      unsubscribe()
    }
  }, [provider, onChangeThread, user?.uuid])

  return { threads }
}
